import React, { Component } from "react";
import cn from "classnames";
import axios, { AxiosRequestConfig } from "axios";
import { FormattedMessage } from "react-intl";

import { aspectRatio as ar } from "../../../../../common/parameters";

import { generatePath, generateAudioPath } from "../../../../paths";
import player from "../../../../player";
import Icon from "../../../../icon";

import Draggable from "../../../Draggable";

import Wrapper from "../../Wrapper";

import MessageDisplay from "../Common/MessageDisplay";
import getBirdsPath from "../Common/getBirdsPath";
import NotEdited from "../Common/NotEdited";

import { SubmitButton, PlayBtn, Label } from "../common-style";

import { getImageDimensions, IGetImageDimensionsThis } from "./editor/ScoreEditor/methods";

import { ScoreWrapper, Drop, Drag, Score, Playground, Asset1Wrapper } from "./style";
import messages from "./messages";
import { isNearAnyDrop } from "./utils";
import { IProps, IState } from "./interfaces";
import { taskThemeColors } from "../Common/ChangeColor/utils";
import { convertToNewColors } from "../../../../utils/convertToNewColors";

class BarSorting extends Component<IProps, IState> {
    static defaultProps = {
        handlePlayCount: () => {},
        handleFailCount: () => {},
    };

    max_zIndex = 1;
    timer: ReturnType<typeof setTimeout>;
    img?: HTMLImageElement
    originalDrags: IState["drags"] = [];
    audioBuffer?: { createNode: (volume: "forte" | "mezzo" | undefined) => AudioBufferSourceNode }
    audioInstance?: AudioBufferSourceNode;
    pauseOffset?: number;
    startedAt?: number;
    constructor(props: IProps) {
        super(props);
        this.state = {
            width: 0,
            height: 0,
            drops: [],
            drags: [],
            zIndex: [],
            dropTarget: null,
            solutions: [],
            incorrectBars: null,
            playing: false,
            submitable: false,
            completed: false,
            scale: 1,
            sameTypes: [],
            top: 0,
            left: 0,
            frameScale: 1,
            transition: false,
            loadingAudio: false,
        };

        if (props.setReset) {
            props.setReset(this.resetTask);
        }
        if (props.saveState) {
            props.saveState(this.saveState);
        }
        if (props.restoreState) {
            props.restoreState(this.restoreState);
        }
        if (props.showSolution) {
            props.showSolution(this.showSolution);
        }
    }

    resetTask = () => {
        this.imgLoaded();
        this.setState({
            submitable: false,
            completed: false,
            incorrectBars: null,
        });
    };
    saveState = () => {
        return {
            drags: this.state.drags,
            zIndex: this.state.zIndex,
            solutions: this.state.solutions,
            incorrectBars: this.state.incorrectBars,
            submitable: this.state.submitable,
            completed: this.state.completed,
        };
    };
    restoreState = (state: IState) => {
        this.setState({
            drags: state.drags,
            zIndex: state.zIndex,
            solutions: state.solutions,
            incorrectBars: state.incorrectBars,
            submitable: state.submitable,
            completed: state.completed,
        });
    };
    showSolution = () => {
        let solutions: IState["solutions"] = [];
        const { drags, drops } = this.state;
        drops.forEach((drop, i) => {
            solutions[i] = i;
            drags[i].t = drop.t;
            drags[i].l = drop.l;
        });
        this.setState({
            solutions,
            drags,
            completed: true,
        });
    };
    componentDidMount() {
        this.forceUpdate();
        window.addEventListener("resize", this.resize);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.resize);
        clearTimeout(this.timer);
        if (this.state.playing) {
            this.play();
        }
    }

    componentDidUpdate() {
        const { task } = this.props;
        if (this.state.playing && !task.data.audio) {
            this.play();
        }
    }

    resize = () => {
        if (!this.img) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(this.setScale, 550);
    };

    imgLoaded = () => {
        const { task } = this.props;
        const { data } = task;

        const { width, zoom } = getImageDimensions.call(this as unknown as IGetImageDimensionsThis);

        if (!data.drags) return;

        let zIndex: number[] = [];

        let top: number, left: number, scale: number;
        top = data.scorePosition!.top || 0;
        left = data.scorePosition!.left || 0;
        scale = data.scale || 1;
        let drops: IState["drops"] = [];
        let sameTypes: IState["sameTypes"] = [];
        data.bars!.forEach((barlines, index) => {
            barlines.forEach((barline, barlineIndex) => {
                if (data.nonBars![index].indexOf(barlineIndex) === -1) {
                    drops.push({
                        t: Math.ceil(top + data.systems!.starts[index] * scale),
                        l: Math.ceil(left + (barlineIndex === 0 ? 0 : barlines[barlineIndex - 1]) * scale),
                        w: Math.ceil((barline - (barlineIndex === 0 ? 0 : barlines[barlineIndex - 1])) * scale),
                        h: Math.ceil(data.systems!.height * scale),
                    });
                    zIndex.push(1);
                }
                data.sameTypes!.forEach((sameType, sameTypeIndex) => {
                    sameType.forEach((sameBar) => {
                        if (sameBar.index === index && sameBar.barIndex === barlineIndex) {
                            if (!sameTypes![sameTypeIndex]) sameTypes![sameTypeIndex] = [];
                            sameTypes![sameTypeIndex].push(drops.length - 1);
                        }
                    });
                });
            });
            if (data.nonBars![index] && data.nonBars![index].indexOf(barlines.length) === -1) {
                drops.push({
                    t: Math.ceil(top + data.systems!.starts[index] * scale),
                    l: Math.ceil(left + (barlines[barlines.length - 1] || 0) * scale),
                    w: Math.floor((width * zoom - (barlines[barlines.length - 1] || 0)) * scale),
                    h: Math.floor(data.systems!.height * scale),
                });
                zIndex.push(1);
                data.sameTypes!.forEach((sameType, sameTypeIndex) => {
                    sameType.forEach((sameBar, sameBarIndex) => {
                        if (sameBar.index === index && sameBar.barIndex === barlines.length) {
                            if (!sameTypes[sameTypeIndex]) sameTypes[sameTypeIndex] = [];
                            sameTypes[sameTypeIndex].push(drops.length - 1);
                        }
                    });
                });
            }
        });
        let drags: IState["drags"] = [];
        drags = JSON.parse(JSON.stringify(drops));
        const solutions: IState["solutions"] = [];

        if (data.drags) {
            drags.forEach((drag, index) => {
                if (data.drags![index]) {
                    drag.l = data.drags![index].l;
                    drag.t = data.drags![index].t;
                }
                solutions.push(null);
            });
        } else {
            drags = [];
        }

        this.originalDrags = JSON.parse(JSON.stringify(drags));

        this.setScale();

        if (this.props.preview) {
            drags.forEach((drag, index) => {
                drag.l = drops[index].l;
                drag.t = drops[index].t;
            });
        }

        this.setState({
            width: this.img!.naturalWidth,
            height: this.img!.naturalHeight,
            top,
            left,
            scale,
            drops,
            drags,
            zIndex,
            solutions,
            sameTypes,
        });
    };

    setScale = () => {
        let frameWidth = this.img!.parentElement!.parentElement!.parentElement!.getBoundingClientRect().width;
        let frameScale = frameWidth / ar.width;
        this.setState({ frameScale });
    };

    changeDragPosition = (drag: number, left: number, top: number) => {
        const { drags } = this.state;
        if (left + drags[drag].w > ar.width) left = ar.width - drags[drag].w;
        if (top + drags[drag].h > ar.height) top = ar.height - drags[drag].h;
        drags[drag].l = Math.ceil(left);
        drags[drag].t = Math.ceil(top);
        this.setState({ drags });
    };

    draggedToDrop = (index: number, dropTarget: number) => {
        const { solutions, drags, drops } = this.state;
        if (typeof solutions[dropTarget] === "number") {
            drags[solutions[dropTarget]!].l = this.originalDrags[solutions[dropTarget]!].l;
            drags[solutions[dropTarget]!].t = this.originalDrags[solutions[dropTarget]!].t;
        }
        solutions[dropTarget] = index;
        drags[index].l = drops[dropTarget].l;
        drags[index].t = drops[dropTarget].t;
        this.setState({ solutions, drags }, () => this.checkReady());
    };

    checkReady = () => {
        const { solutions } = this.state;
        if (solutions.filter(solution => solution === null).length === 0) {
            this.setState({ submitable: true });
        } else {
            this.setState({ submitable: false });
        }
    };

    checkSolutions = () => {
        const { solutions, sameTypes } = this.state;
        let incorrectBars: IState["incorrectBars"] = [];
        solutions.forEach((solution, index) => {
            if (solution !== index) {
                let correctSames = sameTypes.filter((sameType: number[]) => {
                    return sameType.indexOf(index) !== -1;
                });
                if (correctSames.length === 1) {
                    let correctSame = correctSames[0];
                    if (correctSame.indexOf(solution!) !== -1) {
                        return;
                    }
                }
                incorrectBars!.push(solution!);
            }
        });
        if (incorrectBars.length === 0) {
            if (this.props.completed) {
                this.props.completed("barSorting");
            }
        } else {
            this.props.handleFailCount!();
        }
        this.setState({ incorrectBars, completed: incorrectBars.length === 0 });
    };

    draggedFromDrop = (index: number) => {
        const { solutions } = this.state;
        solutions.forEach((solution, solutionIndex) => {
            if (solution === index) {
                solutions[solutionIndex] = null;
            }
        });
        this.setState({ solutions, incorrectBars: null });
    };

    clickDrag = (index: number) => {
        const { solutions, drags, drops } = this.state;
        if (solutions.indexOf(index) === -1) {
            let dropTarget = solutions.indexOf(null);
            solutions[dropTarget] = index;
            drags[index].l = drops[dropTarget].l;
            drags[index].t = drops[dropTarget].t;
        } else {
            solutions[solutions.indexOf(index)] = null;
            drags[index].l = this.originalDrags[index].l;
            drags[index].t = this.originalDrags[index].t;
        }
        this.setState({ solutions, drags, dropTarget: null, incorrectBars: null }, this.checkReady);
    };

    play = () => {
        if (this.state.playing) {
            this.audioInstance!.stop();
            setTimeout(() => {
                this.pauseOffset = Date.now() / 1000 - this.startedAt! - 0.1;
                this.setState({ playing: false });
            }, 100);
            return;
        }
        if (this.audioBuffer) {
            this.startPlaying();
        } else {
            const { task } = this.props;
            const { data } = task;
            let options: AxiosRequestConfig = { responseType: "arraybuffer" };
            this.setState({ loadingAudio: true });
            axios.get(generateAudioPath(data.audio!), options).then(async res => {
                const decodeResult = await player.decodeAudio(res.data);
                if (!decodeResult) return;
                const { createNode } = decodeResult;
                this.audioBuffer = { createNode };
                this.setState({ loadingAudio: false });
                this.startPlaying();
            });
        }
    };
    startPlaying() {
        this.audioInstance = this.audioBuffer!.createNode(undefined);
        this.audioInstance.start(0, this.pauseOffset || 0);
        this.startedAt = Date.now() / 1000 - (this.pauseOffset || 0);
        this.setState({ playing: true });
        this.props.handlePlayCount!();
        this.audioInstance.onended = () => {
            this.pauseOffset = 0;
            this.setState({ playing: false });
        };
    }

    resetAudio = (e: Event) => {
        e.stopPropagation();
        this.pauseOffset = 0;
        this.setState({ playing: false });
    }

    render() {
        const { task, isLow, isDemo, handleHomeworkNext, isEaster, isChristmas } = this.props;
        const { data } = task;
        const {
            width,
            height,
            solutions,
            submitable,
            completed,
            transition,
            scale,
            top,
            left,
            drops,
            drags,
            frameScale,
            zIndex,
            dropTarget,
            incorrectBars,
            playing,
            loadingAudio,
        } = this.state;
        if (!data || !data.drags || data.drags.length === 0)
            return (
                <div>
                    <Wrapper>
                        <NotEdited />
                    </Wrapper>
                </div>
            );
        let { color, audio } = data;
        return (
            <div>
                <Wrapper isDemo={isDemo}>
                    {isLow && (
                        <>
                            <Asset1Wrapper>
                                <img src={getBirdsPath("/barSorting/asset-01.svg")} alt="decoration" />
                            </Asset1Wrapper>
                        </>
                    )}
                    <Playground isLow={!!isLow}>
                        <ScoreWrapper
                            isLow={!!isLow}
                            style={{
                                left,
                                top,
                                width: width * scale,
                                height: height * scale,
                            }}
                        >
                            <Score
                                style={{
                                    transform: `scale(${scale}, ${scale})`,
                                }}
                            >
                                <img
                                    src={generatePath(data.score!)}
                                    ref={img => this.img = img!}
                                    onLoad={this.imgLoaded}
                                    width={width}
                                    height={height}
                                    alt="score"
                                    style={{ mixBlendMode: isLow ? undefined : "multiply" }}
                                />
                            </Score>
                        </ScoreWrapper>
                        {drops.map((drop, index) => {
                            return (
                                <Drop
                                    key={index}
                                    className={cn(dropTarget === index && "Active", completed && "HideOnComplete", isChristmas && "Christmas")}
                                    style={{
                                        top: drop.t,
                                        left: drop.l,
                                        width: drop.w,
                                        height: drop.h,
                                    }}
                                />
                            );
                        })}
                        {drops.length === drags.length &&
                            drags.map((drag, index) => {
                                return (
                                    <Draggable
                                        key={index}
                                        position={{
                                            x: drag.l,
                                            y: drag.t,
                                        }}
                                        scale={frameScale}
                                        onDrag={(left, top) => {
                                            this.changeDragPosition(index, left, top);
                                            let dropTarget = isNearAnyDrop({ drops, left, top });
                                            this.setState({ dropTarget });
                                        }}
                                        onDragStart={() => {
                                            if (solutions.indexOf(index) !== -1) {
                                                this.draggedFromDrop(index);
                                            }
                                            this.setState({ transition: false });
                                            this.checkReady();
                                        }}
                                        transition={transition}
                                        onStop={() => {
                                            if (dropTarget !== null) {
                                                this.draggedToDrop(index, dropTarget);
                                            }
                                            this.setState({ dropTarget: null, transition: true });
                                        }}
                                        onStart={() => {
                                            this.max_zIndex++;
                                            zIndex[index] = this.max_zIndex;
                                            this.setState({ zIndex });
                                        }}
                                        zIndex={zIndex[index]}
                                        onClick={() => {
                                            this.setState({ transition: true });
                                            this.clickDrag(index);
                                        }}
                                        disabled={completed}
                                    >
                                        <Drag
                                            className={cn(
                                                incorrectBars &&
                                                    (incorrectBars.indexOf(index) !== -1 ? "Incorrect" : "Correct"),
                                                completed && "HideOnComplete",
                                                isChristmas && "Christmas"
                                            )}
                                            style={{
                                                width: drag.w,
                                                height: drag.h,
                                                ...(!isChristmas && { backgroundColor: convertToNewColors(color, true) }),
                                                ...(isChristmas && color === taskThemeColors[5] && {
                                                    backgroundColor: "transparent",
                                                })
                                            }}
                                        >
                                            <img
                                                src={generatePath(data.score!)}
                                                ref={img => this.img = img!}
                                                width={width * scale}
                                                height={height * scale}
                                                alt="drag"
                                                style={{
                                                    marginLeft: -drops[index].l + data.scorePosition!.left,
                                                    marginTop: -drops[index].t + data.scorePosition!.top,
                                                }}
                                            />
                                        </Drag>
                                    </Draggable>
                                );
                            })}
                    </Playground>
                    {audio && (
                        <PlayBtn
                            isEaster={isEaster}
                            isLow={isLow}
                            className={cn("button", "button--play")}
                            onClick={() => this.play()}
                            disabled={loadingAudio}
                            resetPlay={(!playing && !!this.pauseOffset) ? this.resetAudio : undefined}
                        >
                            {playing ? <Icon i="pause" /> : <Icon i="play" />}
                            <Label>
                                <FormattedMessage {...messages.play} />
                            </Label>
                        </PlayBtn>
                    )}
                    {submitable && !completed && (!incorrectBars || incorrectBars.length === 0) && (
                        <SubmitButton
                            isEaster={isEaster}
                            isLow={isLow}
                            style={{ top: isLow ? 30 : 6, bottom: "auto" }}
                            className={cn("button")}
                            onClick={this.checkSolutions}
                        >
                            <FormattedMessage {...messages.submit} />
                        </SubmitButton>
                    )}

                    {completed && (
                        <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="completed" handleHomeworkNext={handleHomeworkNext} />
                    )}
                    {incorrectBars && incorrectBars.length > 0 && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="incorrect" />}
                </Wrapper>
            </div>
        );
    }
}

export default BarSorting;
